import React from 'react';
import { Link, useSearchParams } from 'react-router';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import * as styles from './styles';

const errorMessages: Record<string, string> = {
  sso_failed: 'Sign-in failed. Please try again.',
  sso_no_account:
    'No account found for this SSO profile. Please contact your administrator.',
  sso_not_eligible:
    'Your account is not linked to SSO for this organisation. Please contact your administrator.',
  sso_account_locked:
    'Your account is locked. Please contact your administrator.',
};

const spinnerStyle = css`
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const loadingContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
`;

const errorContainer = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
`;

const errorBox = css`
  padding: 0.875rem 1rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  color: #dc2626;
  line-height: 1.5;
`;

const SSOCallbackPage: React.FC = () => {
  const theme = useTheme() as BirdiTheme;
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const errorMessage =
    errorCode !== null
      ? (errorMessages[errorCode] ?? errorMessages.sso_failed)
      : null;

  return (
    <>
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      <div css={styles.scrollableContent}>
        <div css={styles.headerSection}>
          <h1 css={styles.title(theme)}>
            {errorMessage ? 'Sign-in failed' : 'Completing sign-in...'}
          </h1>
        </div>

        {errorMessage ? (
          <div css={errorContainer(theme)}>
            <p css={errorBox}>{errorMessage}</p>
            <p css={styles.helpLink(theme)}>
              <Link to="/sign-in">Return to sign-in</Link>
            </p>
          </div>
        ) : (
          <div css={loadingContainer}>
            <div
              css={[
                spinnerStyle,
                css`
                  color: ${theme.ocean};
                `,
              ]}
            />
            <p css={styles.subtitle(theme)}>
              You&apos;ll be redirected shortly.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default SSOCallbackPage;
